import type { DateField } from "@prismicio/client"

import { DateTime } from "@bounce/date"
import { DEFAULT_LOCALE, formatNumber, useTranslation } from "@bounce/i18n"
import { Button, HeroContainer, Tag, grayBlurDataUrl, imgixCroppedHeroImage } from "@bounce/web-components"

import { getCityCurrency } from "../../../../utils/city"
import type { Interpolations } from "../hooks/useInterpolations"
import { Marquee } from "@/components/LuggageStorage/Marquee/Marquee"
import { useStickyCTA } from "@/contexts/StickyCTAContext"
import type { AggregateRating, CityData } from "@/libs/bounce/types"
import type { FormattedEvent } from "@/libs/prismic/types"
import { useUrl } from "@/libs/router"
import { goToApp, useAppURL } from "@/utils/appURL"

import DEFAULT_IMAGE from "@/assets/img/default_hero_3.png"

type HeroProps = {
  aggregateRating: AggregateRating
  city: CityData
  event: FormattedEvent
  interpolations: Interpolations
  primaryButtonLabel: string
}

const formatDate = (date: DateField) => {
  if (!date) return null

  return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED, { locale: DEFAULT_LOCALE })
}

const formatEventDates = (event: FormattedEvent) => {
  if (!event.startDate || !event.endDate) {
    return null
  }

  const startDate = formatDate(event.startDate)
  const endDate = formatDate(event.endDate)

  return `${startDate} → ${endDate}`
}

const Hero = ({ event, city, interpolations, aggregateRating, primaryButtonLabel }: HeroProps) => {
  const { t, i18n } = useTranslation("marketing-ls-event")

  const { name, image, type, address } = event
  const { translatedName: cityName } = city
  const { getUrl } = useUrl()
  const buildAppURL = useAppURL()
  const { ref: stickyCTARef } = useStickyCTA()

  const url = buildAppURL(getUrl({ path: "/s" }), { query: address })
  const onPressFindLuggageStorage = () => goToApp(url, { cta: "EventHero" })

  const eventDates = formatEventDates(event)
  const eventLocation = event.attendance
    ? t(
        "screen.event.hero.subtitleAttendance",
        "About {{attendance}} people will attend {{eventName}} in {{cityName}}.",
        {
          eventName: name,
          attendance: formatNumber(event.attendance, i18n.language),
          cityName,
        },
      )
    : t("screen.event.hero.subtitleLocation", "{{eventName}} in {{cityName}}.", {
        eventName: name,
        cityName,
      })

  return (
    <>
      <HeroContainer
        image={image || DEFAULT_IMAGE}
        placeholder="blur"
        fallbackBlurDataURL={grayBlurDataUrl}
        imgixParams={{ ...imgixCroppedHeroImage }}
        hideImageOnMobile={false}
        className="mb-px">
        <div className="mb-4 flex flex-row flex-wrap gap-2">
          {!!type && <Tag viewClassName="w-fit" color="grey" text={type} />}
          <Tag viewClassName="w-fit" color="grey" text="Events by PredictHQ" />
        </div>
        <h1 className="title1 break-words">{name}</h1>
        <p className="mt-3 text-sm md:mt-3.5 md:text-base lg:mt-5 xl:text-2xl">
          {eventLocation}
          {!!eventDates && (
            <>
              <br />
              <br />
              {eventDates}
            </>
          )}
        </p>
        <div className="mt-4 flex flex-col md:mt-8 md:w-auto md:max-w-[512px] md:flex-row" ref={stickyCTARef}>
          <Button
            className="mt-4 w-full max-w-full md:mt-3.5 md:w-fit xl:mt-4"
            color="primary"
            onClick={onPressFindLuggageStorage}
            textClassName="line-clamp-1">
            {primaryButtonLabel}
          </Button>
          <Button
            href="#about-event"
            variant="ghost"
            className="mt-4 w-full max-w-full md:mt-3.5 md:w-fit xl:mt-4"
            textClassName="line-clamp-1">
            {t("screen.event.hero.cta", "Learn more")}
          </Button>
        </div>
      </HeroContainer>
      <Marquee price={interpolations.PRICE} aggregateRating={aggregateRating} currency={getCityCurrency(city)} />
    </>
  )
}

export default Hero
